import React, { useState, useEffect, useCallback } from 'react';
import { statusHistoryApi, unitsApi, employeesApi, eventsApi, callsApi, reportsApi } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Filter, Download, Clock, 
  Activity, Calendar, RefreshCw, Printer, Timer,
  TrendingUp, TrendingDown, Minus, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const STATUS_CONFIG = {
  enroute: { label: 'ENROUTE', color: 'text-white', bgColor: 'bg-white/20' },
  on_scene: { label: 'ON SCENE', color: 'text-red-500', bgColor: 'bg-red-500/20' },
  transport: { label: 'TRANSPORTING', color: 'text-yellow-400', bgColor: 'bg-yellow-400/20' },
  at_destination: { label: 'AT DESTINATION', color: 'text-orange-400', bgColor: 'bg-orange-400/20' },
  available: { label: 'AVAILABLE', color: 'text-green-500', bgColor: 'bg-green-500/20' },
  at_post: { label: 'AT POST', color: 'text-gray-400', bgColor: 'bg-gray-400/20' },
};

export default function ReportsPage() {
  const [history, setHistory] = useState([]);
  const [units, setUnits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [unitActivity, setUnitActivity] = useState([]);
  const [responseTimes, setResponseTimes] = useState({ calls: [], average_response_time_ms: 0, total_calls_with_response: 0 });
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    unit_id: 'all',
    start_date: '',
    end_date: '',
    status: 'all'
  });

  const [callsPerHour, setCallsPerHour] = useState(0);
  const [callsPerHourPrev, setCallsPerHourPrev] = useState(0);
  const [stats, setStats] = useState({
    avgDuration: {},
  });

  const fetchReferenceData = useCallback(async () => {
    try {
      const [unitsRes, employeesRes, activityRes, responseRes, perHourRes] = await Promise.all([
        unitsApi.getAll(),
        employeesApi.getAll(),
        statusHistoryApi.getUnitActivity(),
        statusHistoryApi.getResponseTimes(),
        callsApi.getPerHour()
      ]);
      setUnits(unitsRes.data);
      setEmployees(employeesRes.data);
      setUnitActivity(activityRes.data);
      setResponseTimes(responseRes.data);
      setCallsPerHour(perHourRes.data.count);
      setCallsPerHourPrev(perHourRes.data.previous_count);
      setCallsPerHourPrev(perHourRes.data.previous_count);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.unit_id && filters.unit_id !== 'all') params.unit_id = filters.unit_id;
      if (filters.start_date) params.start_date = new Date(filters.start_date).toISOString();
      if (filters.end_date) {
        const endDate = new Date(filters.end_date);
        endDate.setHours(23, 59, 59, 999);
        params.end_date = endDate.toISOString();
      }
      
      const response = await statusHistoryApi.getAll(params);
      let data = response.data;
      
      if (filters.status && filters.status !== 'all') {
        data = data.filter(h => h.new_status === filters.status);
      }
      
      setHistory(data);
      calculateStats(data);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load status history');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const calculateStats = (data) => {
    const sortedData = [...data].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    const unitHistories = {};
    sortedData.forEach(h => {
      if (!unitHistories[h.unit_id]) unitHistories[h.unit_id] = [];
      unitHistories[h.unit_id].push(h);
    });
    
    const statusDurations = {};
    Object.values(unitHistories).forEach(unitHistory => {
      for (let i = 0; i < unitHistory.length; i++) {
        const current = unitHistory[i];
        const next = unitHistory[i + 1];
        if (current.new_status) {
          const duration = next
            ? new Date(next.timestamp) - new Date(current.timestamp)
            : new Date() - new Date(current.timestamp);
          if (!statusDurations[current.new_status]) statusDurations[current.new_status] = [];
          statusDurations[current.new_status].push(duration);
        }
      }
    });
    
    const avgDuration = {};
    Object.entries(statusDurations).forEach(([status, durations]) => {
      if (durations.length > 0) {
        avgDuration[status] = durations.reduce((a, b) => a + b, 0) / durations.length;
      }
    });
    
    setStats({ avgDuration });
  };

  const formatDuration = (ms) => {
    if (!ms || ms < 0) return '--';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatResponseTime = (ms) => {
    if (!ms || ms < 0) return '--';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  useEffect(() => { fetchReferenceData(); }, [fetchReferenceData]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // WebSocket for real-time updates
  useWebSocket(useCallback((msg) => {
    if (['unit_status_change', 'call_created', 'call_updated', 'assignment_created'].includes(msg.type)) {
      fetchHistory();
      fetchReferenceData();
    }
  }, [fetchHistory, fetchReferenceData]));

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleExportCSV = () => {
    if (history.length === 0) { toast.error('No data to export'); return; }
    const headers = ['Date', 'Unit', 'Personnel', 'Status', 'Time Placed in Status', 'Destination'];
    const rows = history.map(h => {
      const timestamp = new Date(h.timestamp);
      return [
        timestamp.toLocaleDateString(),
        h.unit_id,
        (h.personnel || []).join('; '),
        getStatusLabel(h.new_status),
        timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
        h.transport_destination || ''
      ];
    });
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `status_history_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported');
  };

  // Generate Unit History PDF - uses main filters
  const handleGeneratePDF = async () => {
    setPdfLoading(true);
    try {
      const params = {};
      if (filters.unit_id && filters.unit_id !== 'all') params.unit_id = filters.unit_id;
      if (filters.start_date) params.start_date = new Date(filters.start_date).toISOString();
      if (filters.end_date) {
        const endDate = new Date(filters.end_date);
        endDate.setHours(23, 59, 59, 999);
        params.end_date = endDate.toISOString();
      }
      
      const response = await statusHistoryApi.getUnitReport(params);
      const reportData = response.data;
      
      if (reportData.length === 0) {
        toast.error('No data found for the selected filters');
        setPdfLoading(false);
        return;
      }
      
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
      
      // Header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('OSMC Communication Division', 14, 15);
      doc.setFontSize(12);
      doc.text('Unit History Report', 14, 22);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      doc.text(`Generated: ${dateStr}`, 14, 28);
      
      let yPos = 32;
      if (filters.unit_id && filters.unit_id !== 'all') {
        doc.text(`Unit: ${filters.unit_id}`, 14, yPos);
        yPos += 4;
      }
      if (filters.start_date || filters.end_date) {
        doc.text(`Date Range: ${filters.start_date || 'Start'} - ${filters.end_date || 'Present'}`, 14, yPos);
        yPos += 4;
      }
      
      const fmtTime = (ts) => {
        if (!ts) return '--';
        return new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
      };
      
      const tableData = reportData.map(row => [
        row.date ? new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '--',
        `${row.unit_id}\n${(row.personnel || []).join(', ')}`,
        row.call_type || '--',
        fmtTime(row.time_assigned),
        fmtTime(row.enroute),
        fmtTime(row.on_scene),
        fmtTime(row.transport),
        fmtTime(row.at_destination),
        row.transport_destination || '--',
        fmtTime(row.available),
        fmtTime(row.at_post),
      ]);

      autoTable(doc, {
        startY: yPos + 2,
        head: [['Date', 'Unit / Personnel', 'Call Type', 'Time Assigned', 'Enroute', 'On Scene', 'Transporting', 'At Designation', 'Transported To', 'Available', 'At Post']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [30, 30, 30], textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold' },
        bodyStyles: { fontSize: 7, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 30 },
          2: { cellWidth: 20 },
          3: { cellWidth: 20 },
          4: { cellWidth: 18 },
          5: { cellWidth: 18 },
          6: { cellWidth: 18 },
          7: { cellWidth: 22 },
          8: { cellWidth: 22 },
          9: { cellWidth: 18 },
          10: { cellWidth: 18 },
        },
        styles: { overflow: 'linebreak' },
        didDrawPage: (data) => {
          // Footer on each page
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.text(`OSMC Communication Division - Page ${doc.internal.getCurrentPageInfo().pageNumber}`, 14, doc.internal.pageSize.height - 8);
        }
      });
      
      doc.save(`unit_history_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF generated');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleGenerateDailySummary = async () => {
    setSummaryLoading(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = new Date(filters.start_date).toISOString();
      if (filters.end_date) {
        const endDate = new Date(filters.end_date);
        endDate.setHours(23, 59, 59, 999);
        params.end_date = endDate.toISOString();
      }

      const response = await reportsApi.getDailySummary(params);
      const data = response.data;

      if (data.total_calls === 0) {
        toast.error('No calls found for the selected date range');
        setSummaryLoading(false);
        return;
      }

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const pageW = doc.internal.pageSize.getWidth();

      // Header
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('OSMC Communication Division', 14, 18);
      doc.setFontSize(13);
      doc.text('Daily Summary Report', 14, 26);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const rangeLabel = filters.start_date || filters.end_date
        ? `${filters.start_date || 'Start'} — ${filters.end_date || 'Present'}`
        : 'All Time';
      doc.text(`Period: ${rangeLabel}    |    Generated: ${new Date().toLocaleString()}`, 14, 32);

      let y = 40;

      // --- Overview Box ---
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(14, y, pageW - 28, 22, 2, 2, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(`Total Calls: ${data.total_calls}`, 20, y + 8);
      doc.text(`Calls w/ Response: ${data.calls_with_response}`, 80, y + 8);
      const avgResp = data.avg_response_time_ms > 0
        ? `${Math.floor(data.avg_response_time_ms / 60000)}m ${Math.floor((data.avg_response_time_ms % 60000) / 1000)}s`
        : '--';
      doc.text(`Avg Response Time: ${avgResp}`, 150, y + 8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      y += 30;

      // --- Calls by Type ---
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Calls by Type', 14, y);
      y += 2;
      const typeRows = Object.entries(data.by_type).sort((a, b) => b[1] - a[1]).map(([t, c]) => [t, String(c)]);
      autoTable(doc, {
        startY: y,
        head: [['Call Type', 'Count']],
        body: typeRows,
        theme: 'grid',
        headStyles: { fillColor: [30, 30, 30], textColor: [255, 255, 255], fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 30 } },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 10;

      // --- Calls by Disposition ---
      if (Object.keys(data.by_disposition).length > 0) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Calls by Disposition', 14, y);
        y += 2;
        const dispRows = Object.entries(data.by_disposition).sort((a, b) => b[1] - a[1]).map(([d, c]) => [d, String(c)]);
        autoTable(doc, {
          startY: y,
          head: [['Disposition', 'Count']],
          body: dispRows,
          theme: 'grid',
          headStyles: { fillColor: [30, 30, 30], textColor: [255, 255, 255], fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 30 } },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      // --- Calls by Hour ---
      if (Object.keys(data.by_hour).length > 0) {
        if (y > 230) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Calls by Hour', 14, y);
        y += 2;
        const hourRows = Object.entries(data.by_hour).map(([h, c]) => [h, String(c)]);
        autoTable(doc, {
          startY: y,
          head: [['Hour', 'Calls']],
          body: hourRows,
          theme: 'grid',
          headStyles: { fillColor: [30, 30, 30], textColor: [255, 255, 255], fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 30 } },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      // --- Unit Utilization ---
      if (data.unit_utilization.length > 0) {
        if (y > 230) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Unit Utilization', 14, y);
        y += 2;
        const unitRows = data.unit_utilization.map(u => [u.unit_id, String(u.jobs)]);
        autoTable(doc, {
          startY: y,
          head: [['Unit', 'Jobs']],
          body: unitRows,
          theme: 'grid',
          headStyles: { fillColor: [30, 30, 30], textColor: [255, 255, 255], fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 30 } },
          margin: { left: 14, right: 14 },
        });
      }

      // Footer on all pages
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(`OSMC Communication Division - Daily Summary - Page ${i}/${totalPages}`, 14, doc.internal.pageSize.height - 8);
      }

      doc.save(`daily_summary_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Daily Summary PDF generated');
    } catch (error) {
      console.error('Error generating daily summary:', error);
      toast.error('Failed to generate Daily Summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const formatDate = (timestamp) => new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatTime = (timestamp) => new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  const getStatusLabel = (status) => STATUS_CONFIG[status]?.label || status?.toUpperCase() || 'UNKNOWN';
  const getStatusColor = (status) => STATUS_CONFIG[status]?.color || 'text-white';
  const getStatusBgColor = (status) => STATUS_CONFIG[status]?.bgColor || 'bg-zinc-700';

  const uniqueUnitIds = [...new Set(units.map(u => u.unit_id))].sort();

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-6" data-testid="reports-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <FileText className="w-7 h-7 md:w-8 md:h-8 text-blue-500" />
              Status History Reports
            </h1>
            <p className="text-zinc-400 mt-1 text-sm">Track and analyze unit status changes</p>
          </div>
          <Button 
            onClick={handleExportCSV}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="export-csv-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded">
                  <Activity className="w-4 h-4 md:w-5 md:h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-zinc-400 text-xs md:text-sm">Calls / Hour</p>
                  <div className="flex items-center gap-2">
                    <p className="text-white text-xl md:text-2xl font-bold" data-testid="calls-per-hour">{callsPerHour}</p>
                    {callsPerHour > callsPerHourPrev ? (
                      <TrendingUp className="w-4 h-4 text-red-400" data-testid="trend-up" />
                    ) : callsPerHour < callsPerHourPrev ? (
                      <TrendingDown className="w-4 h-4 text-green-400" data-testid="trend-down" />
                    ) : (
                      <Minus className="w-4 h-4 text-zinc-500" data-testid="trend-flat" />
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded">
                  <Timer className="w-4 h-4 md:w-5 md:h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-zinc-400 text-xs md:text-sm">Avg Response</p>
                  <p className="text-white text-xl md:text-2xl font-bold" data-testid="avg-response-time">
                    {formatResponseTime(responseTimes.average_response_time_ms)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded">
                  <Activity className="w-4 h-4 md:w-5 md:h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-zinc-400 text-xs md:text-sm">Calls w/ Response</p>
                  <p className="text-white text-xl md:text-2xl font-bold">{responseTimes.total_calls_with_response}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-zinc-400 text-xs md:text-sm">Date Range</p>
                  <p className="text-white text-xs md:text-sm font-medium">
                    {filters.start_date && filters.end_date 
                      ? `${filters.start_date} - ${filters.end_date}`
                      : 'All Time'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900 border-zinc-700 mb-6">
          <CardHeader className="border-b border-zinc-700 pb-4">
            <CardTitle className="font-heading text-base md:text-lg text-white flex items-center gap-2">
              <Filter className="w-5 h-5 text-blue-500" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm">Start Date</Label>
                <Input type="date" value={filters.start_date} onChange={(e) => handleFilterChange('start_date', e.target.value)} className="bg-zinc-800 border-zinc-600 text-white" data-testid="start-date-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm">End Date</Label>
                <Input type="date" value={filters.end_date} onChange={(e) => handleFilterChange('end_date', e.target.value)} className="bg-zinc-800 border-zinc-600 text-white" data-testid="end-date-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm">Unit</Label>
                <Select value={filters.unit_id} onValueChange={(val) => handleFilterChange('unit_id', val)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white" data-testid="unit-filter-select"><SelectValue placeholder="All Units" /></SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-600">
                    <SelectItem value="all" className="text-white">All Units</SelectItem>
                    {uniqueUnitIds.map(uid => (<SelectItem key={uid} value={uid} className="text-white hover:bg-zinc-700">{uid}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm">Status</Label>
                <Select value={filters.status} onValueChange={(val) => handleFilterChange('status', val)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white" data-testid="status-filter-select"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-600">
                    <SelectItem value="all" className="text-white">All Statuses</SelectItem>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (<SelectItem key={key} value={key} className={`${config.color} hover:bg-zinc-700`}>{config.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm opacity-0 hidden md:block">Actions</Label>
                <div className="flex gap-2">
                  <Button onClick={fetchHistory} className="bg-blue-600 hover:bg-blue-700 flex-1" data-testid="apply-filters-button"><RefreshCw className="w-4 h-4 mr-1" />Apply</Button>
                  <Button onClick={() => setFilters({ unit_id: 'all', start_date: '', end_date: '', status: 'all' })} variant="outline" className="border-zinc-600 text-zinc-300" data-testid="clear-filters-button">Clear</Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unit History PDF Generator */}
        <Card className="bg-zinc-900 border-zinc-700 mb-6">
          <CardHeader className="border-b border-zinc-700 pb-4">
            <CardTitle className="font-heading text-base md:text-lg text-white flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-500" />
              Unit History Report
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-zinc-400 text-sm mb-4">Generate a printable PDF using the filters above. Includes Date, Unit/Personnel, Call Type, all status timestamps, and Transported To location.</p>
            <Button onClick={handleGeneratePDF} disabled={pdfLoading} className="bg-blue-600 hover:bg-blue-700" data-testid="generate-pdf-button">
              <Printer className="w-4 h-4 mr-2" />
              {pdfLoading ? 'Generating...' : 'Generate PDF'}
            </Button>
          </CardContent>
        </Card>

        {/* Daily Summary PDF Generator */}
        <Card className="bg-zinc-900 border-zinc-700 mb-6">
          <CardHeader className="border-b border-zinc-700 pb-4">
            <CardTitle className="font-heading text-base md:text-lg text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-500" />
              Daily Summary Report
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-zinc-400 text-sm mb-4">Generate a comprehensive PDF summary using the date filters above. Includes total calls, calls by type, dispositions, busiest hours, response times, and unit utilization.</p>
            <Button onClick={handleGenerateDailySummary} disabled={summaryLoading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="generate-daily-summary-button">
              <BarChart3 className="w-4 h-4 mr-2" />
              {summaryLoading ? 'Generating...' : 'Generate Daily Summary'}
            </Button>
          </CardContent>
        </Card>

        {/* Average Time Per Status, Response Times & Most Active Units */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader className="border-b border-zinc-700 pb-3">
              <CardTitle className="font-heading text-base md:text-lg text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Avg Time Per Status
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${config.bgColor}`}></div>
                      <span className={`text-xs md:text-sm ${config.color}`}>{config.label}</span>
                    </div>
                    <span className="text-white font-mono text-xs md:text-sm font-medium">{formatDuration(stats.avgDuration?.[key])}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader className="border-b border-zinc-700 pb-3">
              <CardTitle className="font-heading text-base md:text-lg text-white flex items-center gap-2">
                <Timer className="w-5 h-5 text-emerald-500" />
                Response Times
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {responseTimes.calls.length === 0 ? (
                <p className="text-zinc-500 text-sm">No response data yet</p>
              ) : (
                <ScrollArea className="h-[180px]">
                  <div className="space-y-2">
                    {responseTimes.calls.map((rt, idx) => (
                      <div key={rt.call_id || idx} className="flex items-center justify-between bg-zinc-800 p-2 rounded-sm">
                        <div>
                          <span className="text-amber-400 text-sm font-medium">{rt.call_type}</span>
                          <span className="text-zinc-500 text-xs block">{rt.location}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-white font-mono text-sm">{formatResponseTime(rt.response_time_ms)}</span>
                          <span className="text-zinc-500 text-xs block">{rt.unit_id}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader className="border-b border-zinc-700 pb-3">
              <CardTitle className="font-heading text-base md:text-lg text-white">Most Active Units</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {unitActivity.length === 0 ? (
                <p className="text-zinc-500 text-sm">No activity recorded</p>
              ) : (
                <ScrollArea className="h-[180px]">
                  <div className="space-y-2">
                    {unitActivity.slice(0, 10).map((item, index) => (
                      <div key={`${item.unit_db_id}-${item.event_id}`} className="flex items-center justify-between bg-zinc-800 p-2 rounded-sm">
                        <div className="flex items-center gap-2">
                          <Badge className={index === 0 ? 'bg-amber-500/20 text-amber-500' : 'bg-zinc-700 text-zinc-400'}>#{index + 1}</Badge>
                          <span className="text-blue-500 font-bold text-sm">{item.unit_id}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-zinc-300 text-sm">{item.job_count} job{item.job_count !== 1 ? 's' : ''}</span>
                          <span className="text-zinc-500 text-xs block">{item.event_name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Status History Log */}
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader className="border-b border-zinc-700 pb-4">
            <CardTitle className="font-heading text-base md:text-lg text-white flex items-center justify-between">
              <span>Status History Log</span>
              <Badge className="bg-zinc-700 text-zinc-300">{history.length} records</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-zinc-800 border-b border-zinc-700 text-zinc-400 text-xs md:text-sm font-medium">
              <div className="col-span-2">DATE</div>
              <div className="col-span-4">UNIT / PERSONNEL</div>
              <div className="col-span-3">STATUS</div>
              <div className="col-span-3">TIME PLACED</div>
            </div>
            <ScrollArea className="h-[350px]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : history.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
                  <FileText className="w-12 h-12 mb-3 opacity-50" />
                  <p>No status history found</p>
                  <p className="text-sm">Try adjusting your filters</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {history.map((record, index) => {
                    const statusLabel = record.transport_destination 
                      ? `${getStatusLabel(record.new_status)} / ${record.transport_destination}`
                      : getStatusLabel(record.new_status);
                    const personnelStr = (record.personnel || []).join(', ') || 'N/A';
                    
                    return (
                      <div key={record.id || index} className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-zinc-800/50 transition-colors" data-testid={`history-row-${index}`}>
                        <div className="col-span-2 text-zinc-300 text-xs md:text-sm">{formatDate(record.timestamp)}</div>
                        <div className="col-span-4">
                          <Badge className="bg-blue-500/20 text-blue-400 w-fit text-xs">{record.unit_id}</Badge>
                          <span className="text-zinc-500 text-xs block mt-1 truncate">{personnelStr}</span>
                        </div>
                        <div className="col-span-3">
                          <span className={`text-xs md:text-sm px-2 py-1 rounded ${getStatusBgColor(record.new_status)} ${getStatusColor(record.new_status)}`}>{statusLabel}</span>
                        </div>
                        <div className="col-span-3 text-zinc-300 font-mono text-xs md:text-sm">{formatTime(record.timestamp)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
